import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Backdrop, Fade } from "@mui/material";
import { useDialog } from "app/hook";
import { AppDispatch, RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { CloseBtn } from "common/elements";
import { usePlaySound, useScale } from "common/utils";
import { PlanType } from "features/subscription/types";
import { updateAiChatUsage } from "features/subscription";
import { usePlan, usePlanLimits } from "features/subscription/hooks";
import { sendMessageSSE } from "./api";
import { Message, MessageRole } from "./types";
import { ChatHeader, ChatInput, MessageArea } from "./components";
import { actions, selectCurrentSessionId, selectIsTyping, selectMessages } from "./assistantSlice";

export const AssistantDialog = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const plan = usePlan();
  const limits = usePlanLimits();
  const assistant = useSelector((state: RootState) => state.config.dialog.assistant);
  const active = useSelector((state: RootState) => state.user.active);
  const aiChatEnabled = useSelector((state: RootState) => state.config.userConfig.ai_chat);
  const messages = useSelector(selectMessages);
  const isTyping = useSelector(selectIsTyping);
  const currentSessionId = useSelector(selectCurrentSessionId);
  const greetingShown = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 使用状況（Redux storeから取得）
  const usageInfo = {
    used: limits.aiChatDaily.used,
    remaining: limits.aiChatDaily.remaining,
    limit: limits.aiChatDaily.limit,
    isUnlimited: limits.aiChatDaily.unlimited,
  };

  // 制限エラーのstate
  const [limitError, setLimitError] = useState<string | null>(null);

  // ダイアログが開いた時に制限チェック、閉じたらリセット
  useEffect(() => {
    if (!assistant.value) return;

    // 制限に達している場合、エラーメッセージを表示
    if (
      (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
      !usageInfo.isUnlimited &&
      usageInfo.remaining <= 0
    ) {
      setLimitError(I18n.t("MSG_ASSISTANT_LIMIT_REACHED"));
    }

    return () => {
      setLimitError(null);
    };
  }, [assistant.value, plan, usageInfo.isUnlimited, usageInfo.remaining]);

  // ダイアログが開いた時にローカルで挨拶メッセージを表示（DBには保存しない）
  useEffect(() => {
    if (!assistant.value || greetingShown.current) return;
    if (messages.length > 0) return;

    greetingShown.current = true;

    // 保護者許可チェック
    if (!aiChatEnabled) {
      setLimitError(I18n.t("MSG_ASSISTANT_PARENT_NOT_ALLOWED"));
      return;
    }

    const greeting: Message = {
      id: `greeting-${Date.now()}`,
      sessionId: "",
      role: MessageRole.ASSISTANT,
      content: I18n.t("MSG_ASSISTANT_GREETING", { name: active?.display_name || "" }),
      createdAt: dayjs().toISOString(),
    };
    dispatch(actions.addMessage(greeting));
  }, [assistant.value, messages.length, dispatch, aiChatEnabled, plan, active?.display_name]);

  const handleClose = () => {
    play();
    greetingShown.current = false;
    dispatch(actions.clearChat());
    handleDialog({ type: DialogType.ASSISTANT, value: false });
  };

  const handleSendMessage = useCallback(
    async (content: string, imageUrls?: string[]) => {
      if (!active?.uid) return;

      // TRIAL/FREEプランで制限達成の場合 → RestrictedFeatureDialog（プラン別対応）
      if (
        (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
        !usageInfo.isUnlimited &&
        usageInfo.used >= usageInfo.limit
      ) {
        handleDialog({
          type: DialogType.RESTRICTED_FEATURE,
          value: true,
          args: { source: RestrictedFeatureSource.AI_CHAT_LIMIT },
        });
        return;
      }

      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        sessionId: currentSessionId || "",
        role: MessageRole.USER,
        content,
        images: imageUrls?.map((url) => ({ url })),
        createdAt: dayjs().toISOString(),
      };
      dispatch(actions.addMessage(userMessage));
      dispatch(actions.setIsTyping(true));
      dispatch(actions.setError(null));

      // AbortControllerを作成
      abortControllerRef.current = new AbortController();

      try {
        await sendMessageSSE({
          uid: active.uid,
          sessionId: currentSessionId || "new",
          message: content,
          imageUrls,
          pageUrl: window.location.href,
          signal: abortControllerRef.current.signal,
          callbacks: {
            onMessageStart: (messageId, sessionId) => {
              // 新規セッションの場合、セッションIDを保存
              if (!currentSessionId) {
                dispatch(actions.setCurrentSession(sessionId));
              }
              const aiMessage: Message = {
                id: messageId,
                sessionId,
                role: MessageRole.ASSISTANT,
                content: "",
                createdAt: dayjs().toISOString(),
              };
              dispatch(actions.addMessage(aiMessage));
            },
            onContentDelta: (delta) => {
              dispatch(actions.updateLastMessage(delta));
            },
            onMessageEnd: (_messageId, usage) => {
              dispatch(actions.setIsTyping(false));
              abortControllerRef.current = null;
              if (usage && active?.uid) {
                dispatch(
                  updateAiChatUsage({
                    uid: active.uid,
                    used: usage.used,
                    remaining: usage.remaining,
                  }),
                );
              }
            },
            onError: (code, message) => {
              dispatch(actions.setIsTyping(false));
              dispatch(actions.setError(message));
              console.error(`AI Chat Error [${code}]:`, message);
              abortControllerRef.current = null;
              // 制限エラーの場合、画面に常時表示
              if (
                code === "RATE_LIMIT_EXCEEDED" ||
                code === "PLAN_NOT_ALLOWED" ||
                code === "QUOTA_EXCEEDED" ||
                code === "ACCESS_DENIED"
              ) {
                setLimitError(message);
              }
            },
          },
        });
      } catch (err) {
        // AbortErrorは正常な停止なのでエラーとして扱わない
        if (err instanceof Error && err.name === "AbortError") {
          dispatch(actions.setIsTyping(false));
        } else {
          dispatch(actions.setIsTyping(false));
          dispatch(actions.setError("メッセージの送信に失敗しました"));
          console.error("Failed to send message:", err);
        }
        abortControllerRef.current = null;
      }
    },
    [
      active?.uid,
      currentSessionId,
      dispatch,
      plan,
      handleDialog,
      usageInfo.used,
      usageInfo.remaining,
      usageInfo.limit,
      usageInfo.isUnlimited,
      aiChatEnabled,
    ],
  );

  const handleStop = useCallback(() => {
    play();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // 空のAIメッセージが残っていたら削除
    dispatch(actions.removeLastEmptyAssistantMessage());
    dispatch(actions.setIsTyping(false));
  }, [dispatch, play]);

  return (
    <Backdrop
      open={assistant.value}
      sx={{
        zIndex: 999999,
        backgroundColor: "transparent",
        opacity: 1,
        backdropFilter: "blur(8px)",
      }}
    >
      <Fade in>
        <div className="absolute inset-0 flex-col-el flex-center bg-textcolor-black/50 pointer-events-auto">
          <div className="flex-col-center" style={{ transform: `scale(${scale})` }}>
            {/* モーダルコンテナ */}
            <div className="relative w-[1100px] h-[800px] border-[6px] border-white rounded-[16px] bg-white shadow-lg">
              <CloseBtn close={handleClose} />

              {/* モーダルコンテンツ */}
              <div className="w-full h-full rounded-[10px] bg-beige-60 overflow-hidden flex flex-col">
                <ChatHeader
                  usageInfo={
                    (plan === PlanType.TRIAL || plan === PlanType.FREE) && !usageInfo.isUnlimited
                      ? usageInfo
                      : null
                  }
                />
                {/* 制限エラー表示 */}
                {limitError && (
                  <div className="flex-row-el justify-center items-center gap-4 py-3 bg-orange-100">
                    <p className="text text-sm text-white">{limitError}</p>
                    <button
                      className="flex-row-el items-center gap-1 px-3 py-1 bg-safe text-white rounded-full text-sm cursor-pointer active:opacity-70 transition-colors border-0"
                      onClick={() => {
                        play();
                        handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: true });
                      }}
                    >
                      <span className="material-symbols-outlined !text-base">sync_alt</span>
                      {I18n.t("MSG_PLAN_CHANGE_BTN")}
                    </button>
                  </div>
                )}
                {/* 会話エリア + 入力エリア一体化コンテナ */}
                <div className="flex-1 flex flex-col mx-4 my-4 bg-white rounded-xl overflow-hidden">
                  <MessageArea messages={messages} isTyping={isTyping} userIcon={active?.icon} />
                  <ChatInput
                    onSend={handleSendMessage}
                    onStop={handleStop}
                    isTyping={isTyping}
                    disabled={!aiChatEnabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Backdrop>
  );
};
